import React from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { CF } from "../../theme/turquoiseTheme.js";
import { CinematicScene } from "./CinematicScene.js";

/**
 * Netflix-style transformation card — artwork fills ~65% of the card.
 *
 * @param {{
 *   title: string,
 *   subtitle?: string,
 *   tagline: string,
 *   sceneId: string,
 *   overlay?: string,
 *   rating?: number,
 *   readMin?: number,
 *   mood?: string[],
 *   ctaLabel?: string,
 *   onPress: () => void,
 *   compact?: boolean,
 * }} props
 */
export function CinematicHeroCard({
  title,
  subtitle,
  tagline,
  sceneId,
  overlay,
  rating,
  readMin,
  mood,
  ctaLabel = "Start Reading",
  onPress,
  compact = false,
}) {
  const heroH = compact ? 160 : 220;

  return (
    <Pressable style={styles.card} onPress={onPress}>
      <CinematicScene sceneId={sceneId} height={heroH} overlay={overlay} />
      <View style={styles.body}>
        <Text style={styles.title} numberOfLines={compact ? 1 : 2}>
          {title}
        </Text>
        {subtitle ? (
          <Text style={styles.subtitle} numberOfLines={1}>
            {subtitle}
          </Text>
        ) : null}
        <View style={styles.metaRow}>
          {rating != null ? <Text style={styles.meta}>★ {rating.toFixed(1)}</Text> : null}
          {readMin != null ? <Text style={styles.meta}>{readMin} min read</Text> : null}
        </View>
        {!compact ? <Text style={styles.tagline}>{tagline}</Text> : null}
        {mood && mood.length > 0 && !compact ? (
          <View style={styles.moodRow}>
            {mood.slice(0, 3).map((m) => (
              <View key={m} style={styles.moodPill}>
                <Text style={styles.moodText}>{m}</Text>
              </View>
            ))}
          </View>
        ) : null}
        <View style={styles.cta}>
          <Text style={styles.ctaText}>{ctaLabel}</Text>
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 20,
    overflow: "hidden",
    marginBottom: 16,
    backgroundColor: "rgba(6, 12, 28, 0.55)",
    borderWidth: 1,
    borderColor: "rgba(94, 234, 212, 0.28)",
  },
  body: {
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 16,
    backgroundColor: "rgba(6, 12, 28, 0.72)",
  },
  title: {
    fontSize: 20,
    fontWeight: "900",
    color: "#FFFFFF",
    letterSpacing: -0.3,
    textShadowColor: "rgba(0,0,0,0.8)",
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 4,
  },
  subtitle: {
    fontSize: 13,
    fontWeight: "600",
    color: CF.primaryBright,
    marginTop: 2,
  },
  metaRow: { flexDirection: "row", gap: 12, marginTop: 8 },
  meta: {
    fontSize: 12,
    fontWeight: "700",
    color: "#D4EEEB",
  },
  tagline: {
    fontSize: 14,
    color: "#E2F5F3",
    lineHeight: 20,
    marginTop: 10,
    fontWeight: "500",
  },
  moodRow: { flexDirection: "row", flexWrap: "wrap", gap: 6, marginTop: 10 },
  moodPill: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: "rgba(45, 212, 191, 0.15)",
    borderWidth: 1,
    borderColor: "rgba(94, 234, 212, 0.3)",
  },
  moodText: { fontSize: 10, fontWeight: "700", color: CF.cyan, letterSpacing: 0.4 },
  cta: {
    marginTop: 14,
    backgroundColor: CF.primaryDark,
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: "center",
    borderWidth: 1,
    borderColor: "rgba(94, 234, 212, 0.45)",
  },
  ctaText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "800",
    letterSpacing: 0.3,
  },
});
